// Retry settings for a voice campaign: what the form holds, what it sends, and
// what it is allowed to promise.
//
// Kept out of the markup because every sentence this produces is a claim about
// whether somebody's phone will ring again, and two of them are claims people
// routinely get backwards:
//
//   * A campaign with NO retry configuration does not retry at all. "Off" is
//     the state most campaigns are already in — it is not a neutral default
//     that quietly redials.
//   * `max_attempts` counts the FIRST call. max_attempts: 2 is one retry, not
//     two. Said in the form, because the alternative is somebody setting 3 and
//     being surprised by two extra calls.
//
// The object is REPLACED on save, never merged — the server stores whatever map
// it is handed. So this module always builds the whole object from the whole
// form; sending only what changed would drop the fields it did not mention.
//
// See campaignRetryRules.test.ts.

/** The two strategies the dialler's delay maths actually branches on. */
export type RetryStrategy = 'FIXED' | 'EXPONENTIAL';

export const RETRY_STRATEGIES: RetryStrategy[] = ['FIXED', 'EXPONENTIAL'];

/** The field name the server lists in `editableFields` and reads in the body. */
export const RETRY_FIELD = 'retry_configuration';

/** The form's own shape. Number boxes hold strings, as number inputs do. */
export interface RetryForm {
  /** Off means: send null, and this campaign never re-dials. */
  on: boolean;
  maxAttempts: string;
  baseDelayMinutes: string;
  strategy: RetryStrategy;
  backoffFactor: string;
  maxDelayMinutes: string;
  respectBusinessHours: boolean;
}

/** What the body carries. Snake_case: the controller's contract. */
export interface RetryApiConfig {
  max_attempts?: number;
  base_retry_delay_minutes?: number;
  retry_strategy?: RetryStrategy;
  backoff_factor?: number;
  max_retry_delay_minutes?: number;
  respect_business_hours?: boolean;
}

/**
 * What switching retry ON proposes.
 *
 * Two attempts in total — one redial — deliberately: turning on a feature
 * should not by itself triple how often a stranger's phone rings. The number is
 * right there to raise.
 */
export const DEFAULT_RETRY_FORM: RetryForm = {
  on: true,
  maxAttempts: '2',
  baseDelayMinutes: '30',
  strategy: 'FIXED',
  backoffFactor: '2',
  maxDelayMinutes: '240',
  respectBusinessHours: true,
};

export const RETRY_OFF_FORM: RetryForm = { ...DEFAULT_RETRY_FORM, on: false };

// ── reading what is stored ──────────────────────────────────────────────────

/**
 * Where the saved config might be, and what it might be called.
 *
 * The two engines do not agree. The voicex runtime's campaign document spells
 * it camelCase (`retryConfiguration`, `maxAttempts`); a cpass campaign stores
 * verbatim the snake_case map we last sent it. Reading only one spelling shows
 * an editor full of defaults over a campaign that is already retrying.
 */
const CONFIG_KEYS = ['retryConfiguration', 'retry_configuration', 'retryConfig', 'retry'];

const firstOf = (o: any, ...keys: string[]): any => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};

/** The stored config object, or null when the campaign carries none. */
export const retryConfigOf = (campaign: any): Record<string, any> | null => {
  const raw = firstOf(campaign, ...CONFIG_KEYS);
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
};

/**
 * Whether the campaign said ANYTHING about retries — even null.
 *
 * A campaign document with no retry key at all is not the same as one that
 * reported an empty config, and the difference matters: the cpass engine's
 * campaign view does not return the retry block, so "off" there may mean "not
 * told" rather than "not set". The form says which it is instead of implying
 * the first.
 */
export const reportsRetry = (campaign: any): boolean =>
  !!campaign && CONFIG_KEYS.some(k => Object.prototype.hasOwnProperty.call(campaign, k));

const numText = (o: Record<string, any>, ...keys: string[]): string => {
  const v = firstOf(o, ...keys);
  if (v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
};

/** EXPONENTIAL or FIXED. The runtime's other words (INCREMENTAL, CUSTOM) are
 *  read as the nearest thing this form can honestly draw — see strategyNote. */
export const readStrategy = (v: unknown): RetryStrategy =>
  String(v ?? '').trim().toUpperCase().startsWith('EXPONENTIAL') ? 'EXPONENTIAL' : 'FIXED';

/**
 * A strategy this form cannot draw, named so it is not silently rewritten.
 *
 * INCREMENTAL and CUSTOM exist on the runtime. Opening the editor on one and
 * saving flattens it to FIXED — worth saying before, not after.
 */
export const strategyNote = (campaign: any): string | null => {
  const cfg = retryConfigOf(campaign);
  if (!cfg) return null;
  const raw = String(firstOf(cfg, 'retry_strategy', 'retryStrategy') ?? '').trim().toUpperCase();
  if (raw === '' || raw === 'FIXED' || raw.startsWith('EXPONENTIAL')) return null;
  return `This campaign is saved as ${raw}, which this form cannot draw. Saving here replaces it with `
    + `${readStrategy(raw)}.`;
};

/** The form as it stands when the editor opens. */
export const retryFormFrom = (campaign: any): RetryForm => {
  const cfg = retryConfigOf(campaign);
  // An empty object is what "no retry" looks like once stored, so it is off.
  if (!cfg || Object.keys(cfg).length === 0) return RETRY_OFF_FORM;
  const bh = firstOf(cfg, 'respect_business_hours', 'respectBusinessHours');
  return {
    on: true,
    maxAttempts: numText(cfg, 'max_attempts', 'maxAttempts', 'max_retry_attempts'),
    baseDelayMinutes: numText(cfg, 'base_retry_delay_minutes', 'baseRetryDelayMinutes',
                              'retry_delay_minutes', 'retryDelayMinutes'),
    strategy: readStrategy(firstOf(cfg, 'retry_strategy', 'retryStrategy')),
    backoffFactor: numText(cfg, 'backoff_factor', 'backoffFactor'),
    maxDelayMinutes: numText(cfg, 'max_retry_delay_minutes', 'maxRetryDelayMinutes'),
    // Absent means true on the server. Reading it as false would draw an
    // unticked box over a campaign that does wait for daylight.
    respectBusinessHours: bh === undefined ? true : String(bh).trim().toLowerCase() !== 'false',
  };
};

// ── what gets sent ──────────────────────────────────────────────────────────

const posInt = (v: string): number | undefined => {
  const s = String(v ?? '').trim();
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
};

const posNum = (v: string): number | undefined => {
  const s = String(v ?? '').trim();
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * The value for `retry_configuration`, whole.
 *
 * null when off — the server reads absent/empty as "never re-dial". A blank box
 * is left out rather than sent as 0: the runtime's own default for an unset
 * field is better than a zero that would read as "zero attempts".
 *
 * backoff_factor and max_retry_delay_minutes are sent only under EXPONENTIAL,
 * because under FIXED they steer nothing — and a saved config that lists them
 * anyway invites somebody to tune a number that cannot move.
 */
export const retryToApi = (form: RetryForm): RetryApiConfig | null => {
  if (!form.on) return null;
  const out: RetryApiConfig = {};
  const attempts = posInt(form.maxAttempts);
  if (attempts !== undefined) out.max_attempts = attempts;
  const base = posInt(form.baseDelayMinutes);
  if (base !== undefined) out.base_retry_delay_minutes = base;
  out.retry_strategy = form.strategy;
  if (form.strategy === 'EXPONENTIAL') {
    const factor = posNum(form.backoffFactor);
    if (factor !== undefined) out.backoff_factor = factor;
    const cap = posInt(form.maxDelayMinutes);
    if (cap !== undefined) out.max_retry_delay_minutes = cap;
  }
  // Always sent. It defaults to true when absent, and the difference between
  // "left at the default" and "deliberately off" is somebody's phone at 4am.
  out.respect_business_hours = form.respectBusinessHours;
  return out;
};

/** Whether saving would change anything, judged on what would actually be sent. */
export const retryChanged = (before: RetryForm, after: RetryForm): boolean =>
  JSON.stringify(retryToApi(before)) !== JSON.stringify(retryToApi(after));

/** Nonsense the server would either refuse or act on in a way nobody wants. */
export const retryProblem = (form: RetryForm): string | null => {
  if (!form.on) return null;
  const attempts = posInt(form.maxAttempts);
  if (attempts !== undefined && attempts < 1) return 'Total attempts cannot be less than 1.';
  const base = posInt(form.baseDelayMinutes);
  if (base !== undefined && base < 1) return 'The wait before a retry must be at least a minute.';
  if (form.strategy === 'EXPONENTIAL') {
    const factor = posNum(form.backoffFactor);
    if (factor !== undefined && factor < 1) {
      return 'A backoff factor below 1 would make each retry come sooner than the last.';
    }
    const cap = posInt(form.maxDelayMinutes);
    if (cap !== undefined && base !== undefined && cap < base) {
      return 'The longest wait is shorter than the first wait, so every retry would use the shorter one.';
    }
  }
  return null;
};

// ── what the form says out loud ─────────────────────────────────────────────

export const RETRY_OFF_NOTE =
  'Off. A call nobody answers is never dialled again — this is what a campaign with no retry settings does.';

/** The server's own fallback when a campaign opts in without naming a number. */
const SERVER_DEFAULT_ATTEMPTS = 2;

const times = (n: number): string => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

/**
 * What `max_attempts` actually buys, spelled out.
 *
 * The single most misread field on this form: it counts the first call. Saying
 * "3 attempts" is no help at all to somebody deciding between 2 and 3, so this
 * says how many EXTRA calls that is.
 */
export const attemptsNote = (maxAttempts: string): string => {
  const n = posInt(maxAttempts);
  if (n === undefined) {
    return `Counts the first call. Left blank, the server retries until ${SERVER_DEFAULT_ATTEMPTS} `
      + 'attempts have been made — one redial.';
  }
  if (n <= 1) return 'Counts the first call, so 1 means no retry at all.';
  const retries = n - 1;
  return `Counts the first call: ${n} means the first call and ${retries === 1 ? 'one retry' : `${retries} retries`}`
    + `, so an unanswered number rings at most ${times(n)}.`;
};

export const businessHoursNote = (respect: boolean): string =>
  respect
    ? 'A retry that falls outside calling hours (09:00–21:00 in India) waits rather than dialling.'
    : 'Retries may go out at any hour, including overnight.';

/** How the delay grows, in the words the delay maths would use. */
export const strategyHelp = (form: RetryForm): string => {
  const base = posInt(form.baseDelayMinutes);
  const b = base === undefined ? 30 : base;
  if (form.strategy === 'FIXED') return `Every retry waits ${b} minutes after the one before.`;
  const factor = posNum(form.backoffFactor);
  const f = factor === undefined || factor < 1 ? 2 : factor;
  const second = Math.round(b * f);
  const cap = posInt(form.maxDelayMinutes);
  const capped = cap === undefined ? '' : `, up to a ceiling of ${cap} minutes`;
  return `The first retry waits ${b} minutes, the next ${second}, each one ×${f} the last${capped}.`;
};

/** One line for the Configuration panel, so the state is legible without opening Edit. */
export const retrySummary = (campaign: any): string => {
  const cfg = retryConfigOf(campaign);
  if (!cfg || Object.keys(cfg).length === 0) return 'off — never re-dialled';
  const form = retryFormFrom(campaign);
  const n = posInt(form.maxAttempts);
  const attempts = n === undefined ? `${SERVER_DEFAULT_ATTEMPTS} attempts (server default)` : `${n} attempts`;
  const delay = form.baseDelayMinutes === '' ? '' : `, ${form.baseDelayMinutes}m apart`;
  const strat = form.strategy === 'EXPONENTIAL' ? ' (backing off)' : '';
  const hours = form.respectBusinessHours ? '' : ', any hour';
  return `${attempts}${delay}${strat}${hours}`;
};
